if (!customElements.get('mega-menu')) {
  customElements.define(
    'mega-menu',
    class MegaMenu extends HTMLElement {
      constructor() {
        super();
        this.updateHeight = this.updateHeight.bind(this);
        this.heightFrame = null;
      }

      static get observedAttributes() {
        return ['open'];
      }

      updateHeight() {
        const flyoutHeight = this.scrollHeight;

        if (flyoutHeight > 0) {
          this.style.setProperty('--flyout-height', `${flyoutHeight}px`);
        }
      }

      scheduleHeightUpdate() {
        if (this.heightFrame !== null) return;
        this.heightFrame = requestAnimationFrame(() => {
          this.heightFrame = null;
          if (this.isConnected) this.updateHeight();
        });
      }

      loadContent() {
        const template = this.querySelector('.template-mega-menu');
        if (!template || this.hasAttribute('loaded')) return;
        const content = template.content.cloneNode(true);
        template.replaceWith(content);
        this.setAttribute('loaded', 'true');
        this.scheduleHeightUpdate();
        this.dispatchEvent(new CustomEvent('mega-menu:loaded', {
          bubbles: true,
          detail: {
            menuElement: this,
            loadedAt: new Date().toISOString()
          }
        }));
      }

      attributeChangedCallback(name) {
        if (name !== 'open' || !this.hasAttribute('open')) return;

        if (this.hasAttribute('loaded')) {
          this.scheduleHeightUpdate();
        } else {
          this.loadContent();
        }
      }

      disconnectedCallback() {
        cancelAnimationFrame(this.heightFrame);
        this.heightFrame = null;
      }
    }
  );
}

function buildMenuItemIndex(root) {
  const menuItems = new Map();
  root.querySelectorAll('.header__inline-menu [data-menu-item]').forEach((link) => {
    const menuItem = link.dataset.menuItem;
    if (!menuItem || menuItems.has(menuItem)) return;

    menuItems.set(menuItem, {
      link,
      menu: link.closest('header-menu')
    });
  });

  return menuItems;
}

function createHeaderMenu(link) {
  const listItem = link.closest('li[role="listitem"]');
  if (!listItem) return null;

  let trigger = link;
  if (link.tagName !== 'BUTTON') {
    trigger = document.createElement('button');
    trigger.type = 'button';
    trigger.id = link.id;
    trigger.className = link.className + ' header__menu-toggle';
    trigger.dataset.menuItem = link.dataset.menuItem || '';
    trigger.innerHTML = link.innerHTML;
    link.replaceWith(trigger);
  }

  if (!trigger.querySelector(':scope > .icon-caret')) {
    trigger.insertAdjacentHTML('beforeend', '<svg class="icon icon-caret" aria-hidden="true" focusable="false"><use href="#icon-menu-caret"></use></svg>');
  }

  const menu = document.createElement('header-menu');
  menu.className = 'header__menu header__menu--has-megamenu header__menu--has-icon block relative';
  menu.setAttribute('level', '1');
  menu.setAttribute('nav-zone', '.header__inline-menu');
  menu.setAttribute('open-trigger', 'click');
  menu.dataset.menuItem = trigger.dataset.menuItem || '';

  const nextSibling = trigger.nextSibling;
  menu.appendChild(trigger);
  listItem.insertBefore(menu, nextSibling);

  return menu;
}

function resetMenuContent(menu) {
  menu.querySelector(':scope > .header__submenu')?.remove();
  menu.querySelector(':scope > mega-menu:not([data-menu-group-item])')?.remove();
}

function buildDrawerMenuIndex(root) {
  const drawerItems = new Map();
  root.querySelectorAll('.menu-drawer [data-menu-item]').forEach((menuDetails) => {
    const menuItem = menuDetails.dataset.menuItem;
    if (!menuItem || drawerItems.has(menuItem)) return;

    drawerItems.set(menuItem, menuDetails);
  });

  return drawerItems;
}

function createDrawerMenuDetails(link) {
  const listItem = link.closest('li');
  if (!listItem) return null;

  const title = link.querySelector(':scope > span')?.textContent ?? link.textContent.trim();
  const uid = link.dataset.menuItem || Math.random().toString(36).slice(2);
  const href = link.getAttribute('href');

  let viewAllHtml = '';
  if (href && href !== '#') {
    const viewAllLabel = link.dataset.menuItem === 'shop-all' ? window.headerStrings.shopAll : window.headerStrings.viewAll;
    viewAllHtml = `<a href="${href}" class="menu-drawer__view-all link link--text link--hover link--underline focus-inset"><span>${viewAllLabel}</span></a>`;
  }

  const menuDetails = document.createElement('menu-details');
  menuDetails.className = 'menu-details';
  menuDetails.dataset.menuItem = link.dataset.menuItem || '';
  menuDetails.innerHTML = `
    <details id="Details-menu-drawer-mega-${uid}" class="menu-drawer__dropdown">
      <summary id="MenuDrawer-mega-${uid}" class="menu-drawer__menu-item list-menu__item list-menu__item--lv1 link link--text list-unstyled focus-inset">
        <span>${title}</span>
        <svg class="icon icon-caret" aria-hidden="true" focusable="false"><use href="#icon-menu-caret"></use></svg>
      </summary>
      <div id="link-mega-${uid}" class="menu-drawer__submenu gradient motion-reduce" tabindex="-1">
        <div class="menu-drawer__heading sticky top-0 w-full flex align-center justify-between">
          <button type="button" class="menu-drawer__button flex align-center gap-1-2rem relative left appearance-none cursor-pointer">
            <svg class="icon icon-caret" aria-hidden="true" focusable="false"><use href="#icon-menu-caret-left"></use></svg>
            <span>${title}</span>
          </button>
          ${viewAllHtml}
        </div>
        <div class="menu-drawer__submenu-scroll scroll-visible scrollbar-none"></div>
      </div>
    </details>
  `;

  listItem.replaceChild(menuDetails, link);
  return menuDetails;
}

function injectDrawerMegaBanner(drawerElement, megaMenu) {
  const menuDetails = drawerElement.tagName === 'MENU-DETAILS'
    ? drawerElement
    : createDrawerMenuDetails(drawerElement);
  if (!menuDetails) return;

  const submenu = menuDetails.querySelector('.menu-drawer__submenu');
  if (!submenu) return;

  let scrollContainer = submenu.querySelector(':scope > .menu-drawer__submenu-scroll');
  if (!scrollContainer) {
    scrollContainer = document.createElement('div');
    scrollContainer.className = 'menu-drawer__submenu-scroll scroll-visible scrollbar-none';

    const menuList = submenu.querySelector(':scope > .menu-drawer__menu');
    if (menuList) scrollContainer.appendChild(menuList);
    submenu.appendChild(scrollContainer);
  }

  if (scrollContainer.querySelector(':scope > .menu-drawer__mega-banner')) return;

  const source = megaMenu.querySelector('.mega-menu__banner')
    || megaMenu.querySelector('template.template-mega-menu')?.content?.querySelector('.mega-menu__banner');
  if (!source) return;

  const banner = source.cloneNode(true);
  banner.classList.add('menu-drawer__mega-banner');
  banner.classList.remove('scroll-visible', 'scrollbar-none');
  scrollContainer.appendChild(banner);
}

function applyMegaMenuState(menu, megaMenu) {
  menu.classList.remove('style-1', 'style-2', 'style-3', 'style-4', 'style-custom');
  if (megaMenu.dataset.megaMenuStyle) menu.classList.add(megaMenu.dataset.megaMenuStyle);
}

function bindMegaMenuLoad(menu, megaMenu) {
  if (megaMenu.hasAttribute('loaded') || megaMenu.hasAttribute('data-load-bound')) return;
  megaMenu.setAttribute('data-load-bound', '');

  const events = menu.getAttribute('open-trigger') === 'click'
    ? ['focusin', 'touchstart', 'click']
    : ['pointerenter', 'focusin', 'touchstart', 'click'];
  const load = () => {
    events.forEach((eventName) => menu.removeEventListener(eventName, load));
    megaMenu.removeAttribute('data-load-bound');
    megaMenu.setAttribute('open', '');
  };

  events.forEach((eventName) => menu.addEventListener(eventName, load, {
    once: true,
    passive: eventName === 'pointerenter' || eventName === 'touchstart'
  }));
}

function injectAllDrawerBanners(megaMenus) {
  const drawerMenuIndex = buildDrawerMenuIndex(document);
  if (!drawerMenuIndex.size) return;

  megaMenus.forEach((megaMenu) => {
    const menuItem = megaMenu.dataset.menuGroupItem;
    if (!menuItem) return;

    const drawerMenuDetails = drawerMenuIndex.get(menuItem);
    if (drawerMenuDetails) injectDrawerMegaBanner(drawerMenuDetails, megaMenu);
  });
}

function watchDrawerContentLoad(megaMenus) {
  const drawer = document.getElementById('Menu-Drawer');
  if (!drawer) return;

  if (drawer.hasAttribute('loaded')) {
    injectAllDrawerBanners(megaMenus);
    return;
  }

  const observer = new MutationObserver(() => {
    if (!drawer.hasAttribute('loaded')) return;
    observer.disconnect();
    injectAllDrawerBanners(megaMenus);
  });
  observer.observe(drawer, { attributes: true, attributeFilter: ['loaded'] });
}

function loadMegaMenu() {
  const megaMenus = document.querySelectorAll('mega-menu[data-menu-group-item]');
  if (!megaMenus.length) return;

  const menuItemIndex = buildMenuItemIndex(document);
  watchDrawerContentLoad(megaMenus);

  megaMenus.forEach((megaMenu) => {
    const menuItem = megaMenu.dataset.menuGroupItem;
    if (!menuItem) return;

    const target = menuItemIndex.get(menuItem);
    if (!target?.link) return;

    let targetMenu = target.menu;
    if (!targetMenu) {
      targetMenu = createHeaderMenu(target.link);
      if (!targetMenu) return;
      target.menu = targetMenu;
    }

    if (megaMenu.parentElement !== targetMenu) {
      resetMenuContent(targetMenu);
      targetMenu.appendChild(megaMenu);
    }

    targetMenu.trigger?.setAttribute('aria-controls', megaMenu.id);

    applyMegaMenuState(targetMenu, megaMenu);
    megaMenu.hidden = false;

    const isOpen = targetMenu.classList.contains('active')
      || targetMenu.trigger?.getAttribute('aria-expanded') === 'true';

    if (isOpen) {
      megaMenu.setAttribute('open', '');
    } else {
      bindMegaMenuLoad(targetMenu, megaMenu);
    }

    if (typeof Shopify !== 'undefined' && Shopify.designMode && !megaMenu.hasAttribute('loaded')) {
      megaMenu.setAttribute('open', '');
    }
  });

  // TEMP START: Force the first mega menu open for styling.
  // Delete this entire block through TEMP END to restore normal behavior.
  // const firstMegaMenu = [...megaMenus].find((megaMenu) => megaMenu.closest('header-menu'));
  // const firstHeaderMenu = firstMegaMenu?.closest('header-menu');

  // if (firstMegaMenu && firstHeaderMenu) {
  //   firstMegaMenu.hidden = false;
  //   firstMegaMenu.setAttribute('open', '');
  //   firstHeaderMenu.classList.add('active');
  //   firstHeaderMenu.setAttribute('aria-expanded', 'true');

  //   firstMegaMenu.style.setProperty('opacity', '1');
  //   firstMegaMenu.style.setProperty('visibility', 'visible');
  //   firstMegaMenu.style.setProperty('pointer-events', 'auto');
  //   firstMegaMenu.style.setProperty('transform', 'none');
  //   firstMegaMenu.style.setProperty('z-index', 'var(--z-index-flat, 1)');
  //   firstMegaMenu.style.setProperty('height', 'auto');
  //   firstMegaMenu.style.setProperty('max-height', 'none');
  //   firstMegaMenu.style.setProperty('margin-top', '0');
  //   firstMegaMenu.style.setProperty('overflow', 'visible');
  // }
  // TEMP END: Force the first mega menu open for styling.
}

loadMegaMenu();

document.addEventListener('shopify:section:load', loadMegaMenu);
